import { AsyncButton, Button, CopyIconButton } from '@components/button';
import { Form, FormSelect, FormTextInput } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { useCreateAPIKeyMutation } from '@generated/graphql';
import { MenuItem, Stack } from '@mui/material';
import { addDays, addHours, addMonths, addYears, formatISO } from 'date-fns';
import { GraphQLError } from 'graphql';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as yup from 'yup';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  scope: yup.string().required('Scope is required'),
  organizationId: yup.string().when('scope', {
    is: 'organization',
    then: (schema) => schema.required('Organization id is required'),
  }),
  workspaceId: yup.string().when('scope', {
    is: 'workspace',
    then: (schema) => schema.required('Workspace id is required'),
  }),
  expiresValue: yup.string(),
  expiresUnit: yup.string().oneOf(['hours', 'days', 'months', 'years']),
});
type FormValues = yup.InferType<typeof validationSchema>;

export const CreateApiKeyModal = ({ onClose }: { onClose: () => void }) => {
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [createAPIKeyMutation] = useCreateAPIKeyMutation({ errorPolicy: 'all' });
  const [submissionErrors, setSubmissionErrors] = useState<GraphQLError[]>([]);
  const handleFormSubmit = async (values: FormValues) => {
    const variables = { name: values.name, scope: values.scope } as {
      name: string;
      scope: string;
      organizationId?: string;
      workspaceId?: string;
      expiresAt?: string;
    };
    if (values.scope === 'organization') {
      variables.organizationId = values.organizationId;
    } else if (values.scope === 'workspace') {
      variables.workspaceId = values.workspaceId;
    }
    if (values.expiresValue) {
      switch (values.expiresUnit) {
        case 'hours':
          variables.expiresAt = formatISO(addHours(new Date(), Number(values.expiresValue)));
          break;
        case 'days':
          variables.expiresAt = formatISO(addDays(new Date(), Number(values.expiresValue)));
          break;
        case 'months':
          variables.expiresAt = formatISO(addMonths(new Date(), Number(values.expiresValue)));
          break;
        case 'years':
          variables.expiresAt = formatISO(addYears(new Date(), Number(values.expiresValue)));
          break;
        default:
          console.error('Unable to parse expiration unit');
      }
    }
    const { data, errors } = await createAPIKeyMutation({ variables });
    if (errors) {
      setSubmissionErrors([...errors]);
    } else {
      setNewApiKey(data?.createAPIKey);
      posthog.capture('user_create_api-key', { scope: values.scope });
    }
  };

  if (newApiKey) {
    return (
      <Stack gap={4}>
        <Typography align="center" variant="subtitle1">
          API Key Created!
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: '400px' }}>
          Copy the API key below and store it securely. You will not be able to access it again.
        </Typography>
        <Group gap={2} sx={{ backgroundColor: 'background.default', p: 2, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ maxWidth: '400px' }}>
            {newApiKey}
          </Typography>
          <CopyIconButton value={newApiKey} />
        </Group>
        <Button fullWidth variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Stack>
    );
  }

  return (
    <Form
      initialValues={{
        name: '',
        scope: 'user',
        expiresValue: '',
        expiresUnit: 'hours',
        organizationId: '',
        workspaceId: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ isValid, handleSubmit, dirty, isSubmitting, values }) => {
        return (
          <Stack>
            <FormTextInput name="name" label="Name *" />
            <FormSelect name="scope" label="Scope *">
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="organization">Organization</MenuItem>
              <MenuItem value="workspace">Workspace</MenuItem>
            </FormSelect>
            {values.scope === 'organization' && (
              <FormTextInput name="organizationId" label="Organization id *" />
            )}
            {values.scope === 'workspace' && (
              <FormTextInput name="workspaceId" label="Workspace id *" />
            )}
            <Typography variant="caption2" sx={{ mb: 1 }}>
              Expiration (leave blank for no expiration)
            </Typography>
            <Group gap={2}>
              <FormTextInput
                name="expiresValue"
                label=""
                type="number"
                sx={{ overflow: 'visible' }}
              />
              <FormSelect name="expiresUnit" label="">
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="months">Months</MenuItem>
                <MenuItem value="years">Years</MenuItem>
              </FormSelect>
            </Group>
            <Stack gap={4}>
              {submissionErrors.map((error) => (
                <Typography key={error.message} variant="caption2" color="error">
                  {error.message}
                </Typography>
              ))}
              <AsyncButton
                fullWidth
                loading={isSubmitting}
                disabled={!(isValid && dirty && !isSubmitting)}
                onClick={handleSubmit}
                data-cy="New-Workspace-Modal-Create-Button"
              >
                Create
              </AsyncButton>
              <Button
                fullWidth
                variant="secondary"
                data-cy="New-Workspace-Modal-Cancel-Button"
                onClick={onClose}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
};
